import { TextField } from "@mui/material";
import { NucleofectionPlate } from "features/WorklistTools/Nucleofection/plates/NucleofectionPlate";
import {
  UploadedSourcePlate,
  UploadSourcePlateDialog,
} from "features/WorklistTools/shared/components/UploadSourcePlateDialog";
import {
  NucleofectionStep,
  PlatePosition,
  WellSelection,
  WorklistTools,
  WorklistValues,
} from "features/WorklistTools/shared/interfaces";
import {
  getColIndexFromSelectedWell,
  getPlateWellIdFromRowAndColIndex,
  getRowIndexFromSelectedWell,
  sortByCol,
} from "features/WorklistTools/shared/WorklistHelpers";
import {
  GetLabwareTypesQuery,
  useGetNucleofectionPlateBarcodeMutation,
  useGetPlatesInfoLazyQuery,
  useGetReservedNucPlateWellsLazyQuery,
} from "graphql/generated/schema-types";
import { useAppReduxDispatch, useAppReduxSelector } from "hooks/reduxHooks";
import React, { useEffect, useState } from "react";
import { ConfirmDialog } from "shared-components/ModalsAndDialogs/ConfirmDialog";
import { Alert, AlertType } from "shared-components/toast";
import styled from "styled-components";
import {
  NucleofectionPlateInfoState,
  NucleofectionToolActions,
} from "../state";
import { NucleofectionInternalAction } from "../state/action";
import { useNucleofectionStateProvider } from "../state/StateProvider";
import { NucleofectionPlateFooter } from "./components/NucleofectionPlateFooter";
import { handleUploadPlate } from "./handlers/HandleUploadPlate";
import { srcPlateToWellSelections } from "./handlers/SrcPlateToWellSelections";

const StyledNucPlateDiv = styled("div")`
  height: 320px;
  width: 100%;
`;

const StyledContainerDiv = styled.div`
  height: calc(100vh - 300px);
  width: 600px;
  overflow: auto;
`;

export const NucleofectionPlatesContainer = (nucPlateTypeInfo: any) => {
  const [uploadPlateDialogOpen, setUploadPlateDialogOpen] = useState(false);
  const { nucleofectionInternalState, nucleofectionDispatch } =
    useNucleofectionStateProvider();
  const [selectedPlateIndex, setSelectedPlateIndex] = useState(0);
  const dispatch = useAppReduxDispatch();
  const [getNucleofectionBarcode] = useGetNucleofectionPlateBarcodeMutation();
  const [labwareTypeDialogOpen, setLabwareTypeDialogOpen] = useState(false);
  const [colorIndex, setColorIndex] = useState(0);

  const { sourcePlateInfo, nucPlateInfo, compoundPlateInfo, worklistValues } =
    useAppReduxSelector(
      (state) => state.WorklistTools.NucleofectionTool.present
    );

  const [getPlateInfo, { data: plateInfo, refetch }] =
    useGetPlatesInfoLazyQuery({
      fetchPolicy: "cache-and-network",
      onCompleted: (data) => {
        handleUploadPlate({
          data,
          setLabwareTypeDialogOpen,
          uploadNucleofectionPlate: true,
          sourcePlateInfo,
          nucPlateInfo,
          dispatch,
          colorIndex,
          setColorIndex,
          selectedPlateIndex,
        });
      },
    });

  const setUploadedNucleofectionPlate = (uploadedPlate: boolean) => {
    nucleofectionDispatch({
      type: NucleofectionInternalAction.SET_UPLOADED_NUCLEOFECTION_PLATE,
      payload: {
        uploadedNucleofectionPlate: uploadedPlate,
      },
    });
    if (uploadedPlate) return;
    dispatch(
      NucleofectionToolActions.CLEAR_NUCLEOFECTION_PLATE({
        index: 0,
      })
    );
  };

  const handleUploadNucPlateClose = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    plate: UploadedSourcePlate | undefined,
    data: GetLabwareTypesQuery | undefined
  ) => {
    setUploadPlateDialogOpen(false);
    getPlateInfo({
      variables: {
        where: {
          plateCreatedId: { eq: plate!.plateCreatedId },
          containers: {
            some: {
              isActive: { eq: true },
            },
          },
        },
      },
    });
    setUploadedNucleofectionPlate(true);
  };

  const handleUploadPlateClick = (index: number) => {
    setUploadPlateDialogOpen(true);
    setSelectedPlateIndex(index);
  };

  const [getReservedNucPlateWellsLazyQuery, { data: nucPlateReservedWells }] =
    useGetReservedNucPlateWellsLazyQuery({
      fetchPolicy: "network-only",
      onCompleted(data) {
        const plates = data?.reservedNucPlateWells ?? [];
        const newWellInfo = [];

        for (const reservedPlate of plates) {
          if (!reservedPlate || !reservedPlate.worklistContents) continue;

          for (const wlc of reservedPlate.worklistContents) {
            const nucWellId = wlc?.destinationWellId;
            if (!nucWellId) continue;

            newWellInfo.push({
              barcode: "",
              rowPos: getRowIndexFromSelectedWell(nucWellId) + 1,
              colPos: getColIndexFromSelectedWell(nucWellId) + 1,
              sampleId: "",
              sampleBarcode: "",
              color: "#454545",
            });
          }
        }

        if (newWellInfo.length) {
          dispatch(
            NucleofectionToolActions.ADD_RESERVED_NUC_WELLS({
              newWellInfo: newWellInfo,
            })
          );
          nucleofectionDispatch({
            type: NucleofectionInternalAction.SET_NUCLEOFECTION_ALREADY_RESERVED,
            payload: {
              isNucPlateAlreadyReserved: true,
            },
          });
          Alert({
            type: AlertType.WARNING,
            message:
              "Warning: Nucleofection plate is being used in an existing reservation.",
          });
        }
      },
    });

  useEffect(() => {
    const barcode = nucPlateInfo[0]?.plateBarcode;
    if (
      !barcode ||
      nucleofectionInternalState.step !==
        NucleofectionStep.SelectSourceToNucleofection
    )
      return;

    getReservedNucPlateWellsLazyQuery({
      variables: { nucBarcode: barcode },
    });
  }, [nucPlateInfo[0]?.plateBarcode]);

  const sortWellInfoByCol = () => {
    const wellInfo = nucPlateInfo[0].wellInfo;
    let sortedWellInfo: WellSelection[] = [];

    for (const usedWell of wellInfo) {
      const usedWellId = getPlateWellIdFromRowAndColIndex(
        usedWell.rowPos - 1,
        usedWell.colPos - 1
      );

      sortedWellInfo.push({
        plateIndex: 0,
        plateWellId: usedWellId,
        isSelectable: true,
      });
    }
    sortedWellInfo = sortByCol(sortedWellInfo);

    const sortedWellInfoIds: string[] = [];
    for (const well of sortedWellInfo) {
      const currWellId = well.plateWellId;
      sortedWellInfoIds.push(currWellId);
    }

    return sortedWellInfoIds; // array of all well ids in wellInfo, sorted by column
  };

  const nextOpenNucWell = (): WellSelection => {
    let sourceToNucEmpty = true;

    if (worklistValues.sourceToNuc.length < 1) {
      const wellInfo = nucPlateInfo[0].wellInfo;

      if (wellInfo.length < 1) {
        return {
          plateIndex: 0,
          plateWellId: "A1",
          isSelectable: true,
        };
      } // else, sourceToNuc empty === true, below code runs
    } else {
      sourceToNucEmpty = false;
    }

    let row = 0;
    let col = 0;
    let currWellId = "A1";

    if (!sourceToNucEmpty) {
      // if sourceToNuc is not empty, start at the last well mapped in sourceToNuc
      const sourceToNuc = worklistValues.sourceToNuc;

      currWellId = sourceToNuc[sourceToNuc.length - 1].destWellId;
      row = getRowIndexFromSelectedWell(currWellId);
      col = getColIndexFromSelectedWell(currWellId);
      if (row === 7) {
        row = 0;
        col++;
      } else {
        row++;
      }
      currWellId = getPlateWellIdFromRowAndColIndex(row, col); // get new currWellId after incrementing well row/col
    }

    const sortedWellInfo: string[] = sortWellInfoByCol();

    while (sortedWellInfo.includes(currWellId)) {
      currWellId = getPlateWellIdFromRowAndColIndex(row, col);

      if (row === 7) {
        row = 0;
        col++;
      } else {
        row++;
      }
    }
    const currWellSelection: WellSelection = {
      plateIndex: 0,
      plateWellId: currWellId,
      isSelectable: true,
    };
    return currWellSelection;
  };

  const arrangeColWiseDynamically = (
    currentWorklistValues: WorklistValues[],
    wellSelections: WellSelection[],
    selectedDestWell: WellSelection,
    destRows: number[],
    destCols: number[],
    destPlatePosition: PlatePosition
  ) => {
    wellSelections = sortByCol(wellSelections);
    const wellMappings: WorklistValues[] = [];
    const sortedWellInfo = sortWellInfoByCol();

    let selectedDestRowPos = getRowIndexFromSelectedWell(
      selectedDestWell.plateWellId
    );
    let selectedDestColPos = getColIndexFromSelectedWell(
      selectedDestWell.plateWellId
    );

    for (let i = 0; i < wellSelections.length; i++) {
      if (selectedDestRowPos >= destRows.length) {
        selectedDestRowPos = 0;
        selectedDestColPos++;
      }
      if (selectedDestRowPos > destRows.length) {
        selectedDestRowPos = 0;
      }

      const selectedDestWellId = getPlateWellIdFromRowAndColIndex(
        selectedDestRowPos,
        selectedDestColPos
      );

      if (sortedWellInfo.includes(selectedDestWellId)) {
        selectedDestRowPos++;
        i--; // decrement i to stay on the same source well
        continue; // skip if well already exists in nuc plate
      }

      const well = wellSelections[i];
      if (well.isSelectable) {
        wellMappings.push({
          sourcePlateIndex: well.plateIndex,
          sourceWellId: well.plateWellId,
          destPlateIndex: selectedDestWell.plateIndex,
          destWellId: getPlateWellIdFromRowAndColIndex(
            selectedDestRowPos,
            selectedDestColPos
          ),
          transferVol: "",
        });
        selectedDestRowPos++;
      }
    }
    return wellMappings;
  };

  const getSelectedWellInfo = (
    wellMapping: any[],
    wellSelections: WellSelection[]
  ) => {
    const wellSelectionsCopy = wellSelections;
    const selectedWellInfo: any[] = [];
    for (let i = 0; i < wellSelectionsCopy.length; i++) {
      const well = wellSelectionsCopy[i];
      if (!well.isSelectable) {
        wellSelections.splice(i, 1);
        i--;
        continue;
      }
      let row = getRowIndexFromSelectedWell(well.plateWellId);
      let col = getColIndexFromSelectedWell(well.plateWellId);

      const index = sourcePlateInfo[0].wellInfo.findIndex(
        (e) => e.rowPos === row + 1 && e.colPos === col + 1
      );

      const copySourceWell = { ...sourcePlateInfo[0].wellInfo[index] };
      row = getRowIndexFromSelectedWell(wellMapping[i].destWellId) + 1;
      col = getColIndexFromSelectedWell(wellMapping[i].destWellId) + 1;
      copySourceWell.rowPos = row;
      copySourceWell.colPos = col;

      selectedWellInfo.push(copySourceWell);
    }
    return selectedWellInfo;
  };

  const handleAddToNucPlate = () => {
    const wellSelections = srcPlateToWellSelections(sourcePlateInfo);

    if (wellSelections.length > 60) {
      Alert({
        type: AlertType.ERROR,
        message: "Mapped Source wells cannot exceed 60",
      });
      return;
    }

    const maxWells =
      (nucPlateInfo[0]?.rows.length ?? 0) * (nucPlateInfo[0]?.cols.length ?? 0);
    const currentWells = nucPlateInfo[0]?.wellInfo?.length ?? 0;
    const sourceCount = wellSelections.length;
    const selectionGreaterThanEmpty = (): boolean => {
      const remainingWells = maxWells - currentWells;
      return sourceCount > remainingWells;
    };
    if (selectionGreaterThanEmpty()) {
      Alert({
        type: AlertType.ERROR,
        message:
          "Selected source well(s) exceed the number of empty wells in the Nucleofection plate",
      });
      return;
    }

    const newSourceToNucMappings = arrangeColWiseDynamically(
      worklistValues.sourceToNuc,
      wellSelections,
      nextOpenNucWell(),
      nucPlateInfo[0].rows,
      nucPlateInfo[0].cols,
      PlatePosition.Nucleofection
    );

    for (const sourceToNucMapping of newSourceToNucMappings) {
      sourceToNucMapping.transferVol = compoundPlateInfo[0].transferVol;
    }

    dispatch(
      NucleofectionToolActions.SRC_TO_NUC_ADDED({
        selections: newSourceToNucMappings,
        wellSelections: getSelectedWellInfo(
          newSourceToNucMappings,
          wellSelections
        ),
      })
    );
  };

  const handlePlateTypeChanged = (index: number, plateTypeInfo: any) => {
    if (sourcePlateInfo[0].plateBarcode !== "") {
      getNucleofectionBarcode({}).then((res) => {
        const nextNucBarcode =
          res.data?.nucleofectionPlateBarcode?.nucleofectionPlateBarcode;
        dispatch(
          NucleofectionToolActions.SET_NUCLEOFECTION_PLATE({
            index,
            plateBarcode: nextNucBarcode,
            rows: plateTypeInfo.plateRows,
            cols: plateTypeInfo.plateCols,
            operatingVol: plateTypeInfo.maxLabwareVolume,
            minOperatingVol: plateTypeInfo.minLabwareVolume,
            labwareTypeCode: plateTypeInfo.labwareTypeCode,
          })
        );
      });
    }
  };

  const handleNumWashesChanged = (index: number, numberOfWashes: number) => {
    dispatch(
      NucleofectionToolActions.UPDATE_NUC_NUM_WASHES({
        index,
        numberOfWashes,
      })
    );
    if (numberOfWashes === 0) {
      dispatch(
        NucleofectionToolActions.UPDATE_NUC_WASHVOL({
          washVol: 0,
        })
      );
    }
  };

  return (
    <StyledContainerDiv>
      {nucPlateInfo.map(
        (nucPlate: NucleofectionPlateInfoState, index: number) => (
          <div key={index} style={{ padding: "20px" }}>
            <React.Fragment>
              <TextField
                variant="outlined"
                style={{ width: "100%", marginBottom: "10px" }}
                disabled
                label="Nucleofection Plate Barcode"
                value={nucPlate.plateBarcode}
                InputProps={{
                  startAdornment: <b>{index + 1}| </b>,
                }}
              />
              <StyledNucPlateDiv>
                <NucleofectionPlate
                  worklistTool={WorklistTools.Nucleofection}
                  index={index}
                  wellInfo={nucPlate.wellInfo}
                  plateRows={nucPlate.rows}
                  plateCols={nucPlate.cols}
                  wellMapping={worklistValues.sourceToNuc}
                  sourcePlateInfo={sourcePlateInfo}
                  sourcePlatePosition={PlatePosition.Source}
                />
              </StyledNucPlateDiv>

              <NucleofectionPlateFooter
                nucPlateInfo={nucPlate}
                nucelofectionStep={nucleofectionInternalState.step}
                handleUploadPlateClick={handleUploadPlateClick}
                setUploadedNucleofectionPlate={setUploadedNucleofectionPlate}
                selectedSystem={
                  nucleofectionInternalState.methodSettings!.selectedSystem
                }
                nucPlateTypeInfo={nucPlateTypeInfo}
                handlePlateTypeChanged={handlePlateTypeChanged}
                addToNucButtonDisabled={
                  sourcePlateInfo[0].plateBarcode.length === 0 ||
                  nucPlate.labwareTypeCode.length === 0 ||
                  worklistValues.sourceToNuc.length > 0
                }
                handleAddToNucPlate={handleAddToNucPlate}
                handleNumWashesChanged={handleNumWashesChanged}
              />
            </React.Fragment>
          </div>
        )
      )}

      <UploadSourcePlateDialog
        isOpen={uploadPlateDialogOpen}
        handleClose={() => setUploadPlateDialogOpen(false)}
        handleUpload={handleUploadNucPlateClose}
        queryVariables={
          nucPlateTypeInfo.nucPlateInfo
            ? {
                where: {
                  labwareTypeId: {
                    in: nucPlateTypeInfo.nucPlateInfo.map((e: any) => {
                      return e.labwareTypeId;
                    }),
                  },
                  plateBarcode: {
                    nin: nucPlateInfo.map((item) => item.plateBarcode),
                  },
                },
              }
            : {}
        }
        isUploadingNucleofection={true}
        title="Select a Nucleofection Plate to Upload"
      ></UploadSourcePlateDialog>
      <ConfirmDialog />
    </StyledContainerDiv>
  );
};
